import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  FaLightbulb,
  FaRocket,
  FaUsers,
  FaChartLine,
  FaBrain,
  FaGraduationCap,
  FaSearch,
  FaStar,
} from 'react-icons/fa';

const SlideCourseIdeas = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const features = [
    {
      icon: <FaLightbulb className="text-lg" />,
      title: t('landing.slideCourseIdeas.features.aiPowered.title', 'AI-Powered Suggestions'),
      description: t('landing.slideCourseIdeas.features.aiPowered.description', 'Personalized ideas and curricula based on your goal and level'),
    },
    {
      icon: <FaRocket className="text-lg" />,
      title: t('landing.slideCourseIdeas.features.instantCreation.title', 'Instant Creation'),
      description: t('landing.slideCourseIdeas.features.instantCreation.description', 'Turn an idea into a full curriculum and modules in minutes'),
    },
    {
      icon: <FaUsers className="text-lg" />,
      title: t('landing.slideCourseIdeas.features.communityHub.title', 'Community Hub'),
      description: t('landing.slideCourseIdeas.features.communityHub.description', 'Discover popular learning paths from the community'),
    },
    {
      icon: <FaChartLine className="text-lg" />,
      title: t('landing.slideCourseIdeas.features.marketInsights.title', 'Market Insights'),
      description: t('landing.slideCourseIdeas.features.marketInsights.description', 'See what people learn most and where demand is growing'),
    },
    {
      icon: <FaBrain className="text-lg" />,
      title: t('landing.slideCourseIdeas.features.smartRecommendations.title', 'Smart Recommendations'),
      description: t('landing.slideCourseIdeas.features.smartRecommendations.description', 'Get next-step recommendations to deepen your mastery'),
    },
    {
      icon: <FaGraduationCap className="text-lg" />,
      title: t('landing.slideCourseIdeas.features.educationalFocus.title', 'Educational Focus'),
      description: t('landing.slideCourseIdeas.features.educationalFocus.description', 'Optimized for learning outcomes, practice, and retention'),
    },
  ];

  const stats = [
    { number: '500+', label: t('landing.slideCourseIdeas.stats.ideasGenerated', 'Ideas Generated') },
    { number: '50+', label: t('landing.slideCourseIdeas.stats.activeCreators', 'Early Creators') },
    { number: '20+', label: t('landing.slideCourseIdeas.stats.courseCategories', 'Course Categories') },
    { number: '90%', label: t('landing.slideCourseIdeas.stats.successRate', 'Success Rate') },
  ];

  const sampleQueries = [
    t('landing.slideCourseIdeas.sample.dataScience', 'Data science fundamentals'),
    t('landing.slideCourseIdeas.sample.french', 'French A2'),
    t('landing.slideCourseIdeas.sample.cyber', 'Cybersecurity basics'),
  ];

  return (
    <main className="w-full bg-white dark:bg-slate-950">
      <div className="relative overflow-hidden py-12 md:py-16 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-gradient-to-b from-white via-indigo-50/30 to-white dark:from-gray-950 dark:via-indigo-950/10 dark:to-gray-950" />
        <div className="relative max-w-6xl mx-auto">
          {/* Hero Section */}
          <section className="mb-20">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-100 dark:bg-blue-900/30 rounded-full border-2 border-blue-300 dark:border-blue-700 mb-6"
              >
                <FaSearch className="text-blue-600 dark:text-blue-400 text-sm" />
                <span className="text-xs font-bold text-blue-700 dark:text-blue-300 uppercase tracking-wide">
                  {t('landing.slideCourseIdeas.hero.badge', 'AI-Powered Discovery')}
                </span>
              </motion.div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-900 dark:text-slate-50 mb-6 leading-tight tracking-tight">
                {t('landing.slideCourseIdeas.hero.title', 'Find your perfect course idea')}
              </h2>
              <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 leading-relaxed mb-12">
                {t('landing.slideCourseIdeas.hero.subtitle', 'Tell us what you want to learn, and we\'ll create a complete curriculum for you instantly')}
              </p>
            </div>

            {/* Search-style discovery mock */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="max-w-2xl mx-auto mb-12"
            >
              <div className="flex flex-col sm:flex-row items-stretch gap-3 p-6 bg-white dark:bg-slate-900 rounded-3xl shadow-xl border-2 border-blue-200 dark:border-blue-700 hover:border-blue-400 dark:hover:border-blue-500 transition-colors duration-300">
                <div className="flex-1 flex items-center gap-4 px-4 py-3">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-400 to-blue-500 flex items-center justify-center text-white font-bold text-xl shadow-lg">
                    ✦
                  </div>
                  <div className="text-start min-w-0 flex-1">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1 font-semibold">{t('landing.slideCourseIdeas.hero.searchLabel', 'What do you want to learn?')}</p>
                    <p className="text-sm font-bold text-slate-900 dark:text-slate-50 truncate">
                      &ldquo;{sampleQueries[0]}&rdquo;, &ldquo;{sampleQueries[1]}&rdquo;, &ldquo;{sampleQueries[2]}&rdquo;
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/course-ideas')}
                  className="px-8 py-4 rounded-2xl font-bold text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 flex items-center justify-center gap-2 shadow-lg transition-colors"
                >
                  <FaSearch className="text-sm" />
                  {t('landing.slideCourseIdeas.hero.exploreButton', 'Explore Ideas')}
                </button>
              </div>
            </motion.div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05, y: -3 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => navigate('/course-ideas')}
                className="inline-flex items-center justify-center gap-2 px-10 py-5 rounded-2xl font-bold text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 shadow-xl shadow-blue-500/30"
              >
                <FaStar className="text-sm" />
                {t('landing.slideCourseIdeas.hero.exploreButton', 'Explore Ideas')}
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05, y: -3 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => navigate('/signup')}
                className="inline-flex items-center justify-center gap-2 px-10 py-5 rounded-2xl font-bold text-slate-900 dark:text-slate-50 bg-white dark:bg-slate-900 border-2 border-slate-200 dark:border-slate-600 hover:border-blue-400 dark:hover:border-blue-500 shadow-md hover:shadow-lg"
              >
                {t('landing.slideCourseIdeas.hero.startButton', 'Start Creating')}
              </motion.button>
            </div>
          </section>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20">
            {stats.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + i * 0.08 }}
                className="text-center p-8 bg-white dark:bg-slate-900 rounded-3xl shadow-lg hover:shadow-xl border-2 border-blue-200 dark:border-blue-700 hover:border-blue-400 dark:hover:border-blue-500 transition-all duration-300"
              >
                <div className="text-2xl sm:text-3xl md:text-4xl font-extrabold bg-gradient-to-r from-blue-500 to-indigo-600 bg-clip-text text-transparent mb-2">{s.number}</div>
                <div className="text-xs md:text-sm text-gray-600 dark:text-gray-400 font-bold">{s.label}</div>
              </motion.div>
            ))}
          </div>

          {/* Features */}
          <section>
            <div className="text-center mb-16">
              <h3 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-900 dark:text-slate-50 mb-4 tracking-tight">
                {t('landing.slideCourseIdeas.featuresSection.title', 'AI-Powered Course Discovery')}
              </h3>
              <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                {t('landing.slideCourseIdeas.featuresSection.subtitle', 'Find the right learning path for your goal')}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((f, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + i * 0.06 }}
                  className="p-8 bg-white dark:bg-slate-900 rounded-3xl shadow-lg hover:shadow-xl border-2 border-slate-200 dark:border-slate-700 hover:border-indigo-400 dark:hover:border-indigo-500 transition-all duration-300"
                >
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-400 to-blue-500 flex items-center justify-center text-white mb-5 shadow-lg">
                    {f.icon}
                  </div>
                  <h4 className="text-lg font-bold text-slate-900 dark:text-slate-50 mb-3">{f.title}</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{f.description}</p>
                </motion.div>
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="mt-20">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="text-center p-14 md:p-16 rounded-3xl bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-2 border-blue-200 dark:border-blue-800"
            >
              <h3 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-900 dark:text-slate-50 mb-4 tracking-tight">
                {t('landing.slideCourseIdeas.cta.title', 'Ready to pick a learning path?')}
              </h3>
              <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 mb-12 max-w-2xl mx-auto">
                {t('landing.slideCourseIdeas.cta.subtitle', 'Start with an idea, then generate your curriculum instantly')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  whileHover={{ scale: 1.05, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/course-ideas')}
                  className="px-10 py-5 rounded-2xl font-bold text-lg text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 shadow-xl shadow-blue-500/30"
                >
                  {t('landing.slideCourseIdeas.cta.exploreButton', 'Browse Ideas')}
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/signup')}
                  className="px-10 py-5 rounded-2xl font-bold text-lg text-slate-900 dark:text-slate-50 bg-white dark:bg-slate-900 border-2 border-slate-200 dark:border-slate-600 hover:border-blue-400 dark:hover:border-blue-500 shadow-md hover:shadow-lg"
                >
                  {t('landing.slideCourseIdeas.cta.createButton', 'Generate Curriculum')}
                </motion.button>
              </div>
            </motion.div>
          </section>
        </div>
      </div>
    </main>
  );
};

export default SlideCourseIdeas;
